import { useEffect, useRef } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { NoopAlgorithm } from './NoopAlgorithm';

export const clusterRenderer = (color) => {
    return {
        render: ({ count, position }) => {
            const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                <circle cx="120" cy="120" stroke="white" stroke-width="4" opacity=".6" r="70" />
            </svg>`;

            return new window.google.maps.Marker({
                position,
                icon: {
                    url: `data:image/svg+xml;base64,${btoa(svg)}`,
                    scaledSize: new window.google.maps.Size(60, 60),
                },
                label: {
                    text: count.toString(),
                    color: 'white',
                    fontFamily: 'Montserrat',
                    fontWeight: 'bold',
                },
                zIndex: 500 + count,
            });
        },
    };
};

// Detach a clusterer being replaced: empty its marker list first so reset()
// cannot unmap markers the successor still manages, then drop it from the map
// to remove its idle listener and any rendered cluster icons.
export const retireClusterer = (clusterer) => {
    clusterer.clearMarkers(true);
    clusterer.setMap(null);
};

export const diffMarkers = (prevSet, markers) => {
    const nextSet = new Set(markers);
    const added = markers.filter((marker) => !prevSet.has(marker));
    const removed = [...prevSet].filter((marker) => !nextSet.has(marker));

    return { nextSet, added, removed };
};

function MarkerCluster({ map, markers = [], color = 'black', disabled = false, callbacks }) {
    const cluster = useRef(null);
    const prevSetRef = useRef(new Set());

    useEffect(() => {
        if (map.current && (!cluster.current || cluster.current.noop !== disabled)) {
            if (cluster.current) {
                retireClusterer(cluster.current);
            }

            cluster.current = new MarkerClusterer({
                map: map.current,
                markers: markers,
                renderer: clusterRenderer(color),
                onClusterClick: callbacks.onClick ? callbacks.onClick : undefined,
                ...(disabled
                    ? {
                          algorithm: new NoopAlgorithm(),
                      }
                    : {}),
            });

            cluster.current.noop = disabled;
            prevSetRef.current = new Set(markers);
        }
    }, [map.current, disabled]);

    useEffect(() => {
        if (map.current && cluster.current) {
            const { nextSet, added, removed } = diffMarkers(prevSetRef.current, markers);
            prevSetRef.current = nextSet;

            if (added.length === 0 && removed.length === 0) {
                return;
            }

            if (removed.length) {
                cluster.current.removeMarkers(removed, true);
            }
            if (added.length) {
                cluster.current.addMarkers(added, true);
            }
            cluster.current.render();
        }
    }, [map, markers]);

    return null;
}

export default MarkerCluster;
